import { useEffect, useState } from "react";

import { AuditLog } from "./AuditLog.jsx";
import { BoardRoster } from "./BoardRoster.jsx";
import { Broadcasts } from "./Broadcasts.jsx";
import { Calendar } from "./Calendar.jsx";
import { ContentManager } from "./ContentManager.jsx";
import { Engagement } from "./Engagement.jsx";
import { Dashboard } from "./Dashboard.jsx";
import { CONTENT_CONFIGS } from "./contentConfigs.js";
import { EventRsvps } from "./EventRsvps.jsx";
import { Finance } from "./Finance.jsx";
import { GroupRoster } from "./GroupRoster.jsx";
import { Meetings } from "./Meetings.jsx";
import { Mentorship } from "./Mentorship.jsx";
import { Members } from "./Members.jsx";
import { Motions } from "./Motions.jsx";
import { ResourceVault } from "./ResourceVault.jsx";
import { Volunteers } from "./Volunteers.jsx";
import { WebsiteHub } from "./WebsiteHub.jsx";

const WRITE_ROLES = ["admin", "president", "secretary"];
const MEMBER_WRITE_ROLES = [...WRITE_ROLES, "membership_chair"];
const FINANCE_ROLES = ["admin", "president", "treasurer"];

const NAV = [
  { key: "overview", label: "Overview", phase: 1 },
  { key: "members", label: "Members", phase: 1 },
  { key: "meetings", label: "Meetings", phase: 1 },
  { key: "calendar", label: "Calendar" },
  { key: "votes", label: "Motions & Votes" },
  { key: "announce", label: "Announcements" },
  { key: "website", label: "Website" },
  { key: "resources", label: "Resource Bucket", phase: 1 },
  { key: "finances", label: "Finances", phase: 2 },
  { key: "compliance", label: "Compliance", phase: 2 },
  { key: "events", label: "Events & Growth", phase: 3 },
  { key: "groups", label: "Working Groups" },
  { key: "mentorship", label: "Mentorship" },
  { key: "people", label: "Volunteers", phase: 4 },
  { key: "audit", label: "Audit Log" },
];

async function api(path, options) {
  const res = await fetch(path, { credentials: "same-origin", ...options });
  return res;
}

function Login() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error

  async function requestLink(e) {
    e.preventDefault();
    setState("sending");
    const res = await api("/api/auth/request-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    setState(res.ok ? "sent" : "error");
  }

  return (
    <div className="center">
      <div className="card">
        <div className="brand">Quorum</div>
        <p className="muted" style={{ marginTop: 4 }}>
          Chapter operations cockpit
        </p>

        {state === "sent" ? (
          <p style={{ marginTop: 20 }}>
            Check your email for a sign-in link.
            {process.env.NODE_ENV === "development" && (
              <>
                <br />
                <span className="muted" style={{ fontSize: 13 }}>
                  In local dev the link is printed to the server log.
                </span>
              </>
            )}
          </p>
        ) : (
          <form onSubmit={requestLink} style={{ marginTop: 20, display: "grid", gap: 12 }}>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              placeholder="you@example.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="btn-primary" type="submit" disabled={state === "sending"}>
              {state === "sending" ? "Sending…" : "Email me a sign-in link"}
            </button>
            {state === "error" && (
              <span className="muted" style={{ fontSize: 13 }}>
                Please enter a valid email address.
              </span>
            )}
          </form>
        )}

        <div className="divider" style={{ margin: "18px 0" }}>
          or
        </div>
        <a href="/api/auth/google/login">
          <button className="btn-ghost" style={{ width: "100%" }} type="button">
            Sign in with Google
          </button>
        </a>
      </div>
    </div>
  );
}

function Shell({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  const [activeId, setActiveId] = useState(user.memberships[0]?.chapter_id ?? null);
  const membership =
    user.memberships.find((m) => m.chapter_id === activeId) || user.memberships[0];
  const active = NAV.find((n) => n.key === tab);

  return (
    <>
      <header className="topbar">
        <span className="brand">Quorum</span>
        {user.memberships.length > 1 ? (
          <select
            value={activeId ?? ""}
            onChange={(e) => setActiveId(Number(e.target.value))}
            style={{ width: "auto" }}
          >
            {user.memberships.map((m) => (
              <option key={m.chapter_id} value={m.chapter_id}>
                {m.chapter} · {m.role}
              </option>
            ))}
          </select>
        ) : membership ? (
          <span className="pill">
            {membership.chapter} · {membership.role}
          </span>
        ) : (
          <span className="pill">no chapter assigned</span>
        )}
        <span style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 13 }}>
          {user.name || user.email}
        </span>
        <a
          className="btn-ghost"
          href="/account/security"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}
          title="Two-factor authentication"
        >
          {user.has_2fa ? "🔒" : ""} Security
        </a>
        <button className="btn-ghost" onClick={onLogout} type="button">
          Sign out
        </button>
      </header>

      <nav className="tabs">
        {NAV.map((n) => (
          <button
            key={n.key}
            className={n.key === tab ? "active" : ""}
            onClick={() => setTab(n.key)}
            type="button"
          >
            {n.label}
          </button>
        ))}
      </nav>

      <main>
        <h2 style={{ marginTop: 0 }}>{active.label}</h2>
        {!membership && (tab === "overview" || tab === "members") ? (
          <p className="muted">
            You're not assigned to a chapter yet. Ask an admin to grant you a role.
          </p>
        ) : tab === "overview" ? (
          <>
            <Dashboard chapterId={membership.chapter_id} />
            <BoardRoster
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          </>
        ) : tab === "members" ? (
          <Members
            chapterId={membership.chapter_id}
            canManage={MEMBER_WRITE_ROLES.includes(membership.role)}
            isAdmin={membership.role === "admin"}
          />
        ) : tab === "meetings" ? (
          membership ? (
            <Meetings
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "calendar" ? (
          membership ? (
            <Calendar chapterId={membership.chapter_id} />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "votes" ? (
          membership ? (
            <Motions
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "announce" ? (
          membership ? (
            <Broadcasts
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "website" ? (
          membership ? (
            <WebsiteHub
              chapterId={membership.chapter_id}
              slug={membership.chapter_slug}
              canManage={WRITE_ROLES.includes(membership.role)}
              isAdmin={membership.role === "admin"}
              onGoToTeam={() => setTab("overview")}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "resources" ? (
          membership ? (
            <ResourceVault
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "events" ? (
          membership ? (
            <>
              <Engagement chapterId={membership.chapter_id} />
              <ContentManager
                chapterId={membership.chapter_id}
                config={CONTENT_CONFIGS.events}
                canManage={WRITE_ROLES.includes(membership.role)}
                detail={EventRsvps}
                detailLabel="RSVPs"
              />
            </>
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "finances" ? (
          membership ? (
            <Finance
              chapterId={membership.chapter_id}
              canManage={FINANCE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "compliance" ? (
          membership ? (
            <ContentManager
              chapterId={membership.chapter_id}
              config={CONTENT_CONFIGS.compliance}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "groups" ? (
          membership ? (
            <ContentManager
              chapterId={membership.chapter_id}
              config={CONTENT_CONFIGS.working_groups}
              canManage={WRITE_ROLES.includes(membership.role)}
              detail={GroupRoster}
              detailLabel="Members"
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "mentorship" ? (
          membership ? (
            <Mentorship
              chapterId={membership.chapter_id}
              canManage={MEMBER_WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "people" ? (
          membership ? (
            <Volunteers
              chapterId={membership.chapter_id}
              canManage={MEMBER_WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : tab === "audit" ? (
          membership ? (
            <AuditLog
              chapterId={membership.chapter_id}
              canManage={WRITE_ROLES.includes(membership.role)}
            />
          ) : (
            <p className="muted">You're not assigned to a chapter yet.</p>
          )
        ) : (
          <p className="muted">
            This module arrives in <strong>Phase {active.phase}</strong>. The shell,
            auth and data model are in place — features land next.
          </p>
        )}
      </main>
    </>
  );
}

export function App() {
  const [user, setUser] = useState(undefined); // undefined=loading, null=anon

  useEffect(() => {
    let cancelled = false;
    api("/api/auth/me").then(async (res) => {
      const value = res.ok ? await res.json() : null;
      if (!cancelled) setUser(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
  }

  if (user === undefined) {
    return <div className="center muted">Loading…</div>;
  }
  if (user === null) {
    return <Login />;
  }
  if (user.must_enrol_2fa) {
    return <Enrol2faGate onLogout={logout} />;
  }
  return <Shell user={user} onLogout={logout} />;
}

function Enrol2faGate({ onLogout }) {
  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 460, textAlign: "center" }}>
        <div className="brand">Quorum</div>
        <h2 style={{ margin: "16px 0 8px" }}>Two-factor authentication required</h2>
        <p className="muted">
          Your chapter now requires committee accounts to use two-factor
          authentication. Set it up to continue to the cockpit — it takes a minute
          with any authenticator app.
        </p>
        <a className="btn-primary" href="/account/security" style={{ display: "block", textDecoration: "none", marginTop: 18 }}>
          Set up two-factor authentication
        </a>
        <button className="btn-ghost" onClick={onLogout} type="button" style={{ marginTop: 10, width: "100%" }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
